import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { JsonLd } from '../components/SEO/JsonLd';
import { buildItemListSchema } from '../utils/seo';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { CUTOFF_START_YEAR, CUTOFF_END_YEAR } from '../constants/years';
import { SITE_CONFIG } from '../config/siteConfig';
import {
  FaMapMarkerAlt, FaSpinner, FaTrophy,
  FaFilter, FaChevronDown, FaChevronUp, FaGraduationCap,
  FaMedal, FaCheck, FaPlus, FaArrowUp, FaArrowDown,
} from 'react-icons/fa';

interface Department {
  branch_name: string;
  branch_code: string;
  avg_closing_rank: number;
  total_seats: number;
  seats_filled: number;
  fill_rate: number;
  trend: 'high_demand' | 'medium_demand' | 'low_demand';
  rank_change_5yr: number;
}

interface CollegeRow {
  rank: number;
  college_id: number;
  college_name: string;
  college_code: string;
  college_type: string;
  district_name: string;
  naac_grade: string | null;
  avg_closing_rank: number;
  total_departments: number;
}

interface DistrictGroup {
  district_id: number;
  district_name: string;
  total_colleges: number;
  colleges: CollegeRow[];
  isExpanded: boolean;
  isLoading: boolean;
  isLoaded: boolean;
}

const communities = ['OC', 'BC', 'BCM', 'MBC', 'SC', 'SCA', 'ST'];
const years = Array.from({ length: CUTOFF_END_YEAR - CUTOFF_START_YEAR + 1 }, (_, i) => CUTOFF_END_YEAR - i);
const collegeTypes = ['Government', 'Anna University', 'Aided', 'Private'];

const shortName = (name: string) => {
  // Strip (Autonomous) and other parenthetical qualifiers first
  let n = name
    .replace(/\(Autonomous\)/gi, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  // Preserve campus identifier (e.g. Anna University CEG/MIT/ACT campuses)
  const campusMatch = n.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
  if (campusMatch) {
    const base = campusMatch[1].split(',')[0].trim();
    return `${base} — ${campusMatch[2]}`;
  }
  // Then cut the address at the first comma (same as /colleges page)
  const commaIdx = n.indexOf(',');
  if (n.length > 55 && commaIdx > 20) return n.substring(0, commaIdx).trim();
  return n;
};

const getCompLabel = (rank: number) => {
  if (rank < 5000)  return { text: 'Very Competitive', color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200' };
  if (rank < 15000) return { text: 'Competitive',      color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' };
  if (rank < 30000) return { text: 'Moderate',         color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' };
  if (rank < 50000) return { text: 'Accessible',       color: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200' };
  return               { text: 'Easy to Get In',   color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200' };
};

const typeColor: Record<string, string> = {
  'Government':             'bg-green-100 text-green-800',
  'Anna University':        'bg-purple-100 text-purple-800',
  'Anna Univ - Constituent':'bg-purple-100 text-purple-800',
  'Govt Aided':             'bg-teal-100 text-teal-800',
  'Aided':                  'bg-teal-100 text-teal-800',
  'Private':                'bg-gray-100 text-gray-700',
};

const naacColor: Record<string, string> = {
  'A++': 'bg-emerald-600 text-white',
  'A+':  'bg-green-600 text-white',
  'A':   'bg-lime-600 text-white',
  'B++': 'bg-blue-600 text-white',
  'B+':  'bg-blue-500 text-white',
  'B':   'bg-sky-500 text-white',
  'C':   'bg-gray-500 text-white',
};

const medalColor = (rank: number) => {
  if (rank === 1) return 'text-yellow-500';
  if (rank === 2) return 'text-gray-400';
  if (rank === 3) return 'text-amber-600';
  return 'text-gray-300';
};

const BestColleges = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'overall' | 'district'>('overall');

  const [community, setCommunity] = useState('OC');
  const [year, setYear]           = useState(CUTOFF_END_YEAR);
  const [collegeType, setCollegeType]   = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');

  const [overall, setOverall]     = useState<CollegeRow[]>([]);
  const [districts, setDistricts] = useState<DistrictGroup[]>([]);
  const [districtOptions, setDistrictOptions] = useState<{ id: number; name: string }[]>([]);
  const [branchOptions, setBranchOptions] = useState<{ branch_code: string; display_name: string }[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // Department expansion
  const [expandedColleges, setExpandedColleges] = useState<Set<number>>(new Set());
  const [deptData, setDeptData]   = useState<Map<string, Department[]>>(new Map());
  const [deptLoading, setDeptLoading] = useState<Set<number>>(new Set());

  // District data cache
  const [districtCache, setDistrictCache] = useState<Map<string, CollegeRow[]>>(new Map());

  // Comparison
  const [selected, setSelected]   = useState<Map<number, any>>(new Map());

  useEffect(() => {
    axios.get('/districts').then(r => setDistrictOptions(r.data?.districts || [])).catch(() => {});
    axios.get('/courses/branches/distinct').then((r: any) => setBranchOptions(r.branches || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
    // Clear expansions when filters change
    setExpandedColleges(new Set());
  }, [community, year, collegeType, districtFilter, branchFilter, tab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'overall') {
        const params: any = { year, community, ranking_method: 'colleges' };
        if (collegeType)    params.college_type = collegeType;
        if (districtFilter) params.district = districtFilter;
        if (branchFilter)   params.branch_code = branchFilter;
        const res = await axios.get('/cutoffs/district-summary', { params });
        const raw: any[] = res.data.districts || [];
        setOverall(raw.map((item: any, i: number) => ({
          rank: i + 1,
          college_id: item.college_id || 0,
          college_name: item.college_name || '',
          college_code: item.college_code || '',
          college_type: item.college_type || '',
          district_name: item.district_name || '',
          naac_grade: item.naac_grade || null,
          avg_closing_rank: item.avg_closing_rank ? Math.round(item.avg_closing_rank) : 0,
          total_departments: item.total_departments || 0,
        })));
      } else {
        const params: any = { year, community, ranking_method: 'quality' };
        if (collegeType)    params.college_type = collegeType;
        if (districtFilter) params.district = districtFilter;
        if (branchFilter)   params.branch_code = branchFilter;
        const res = await axios.get('/cutoffs/district-summary', { params });
        setDistricts((res.data.districts || []).map((d: any) => ({
          district_id:   d.district_id,
          district_name: d.district_name,
          total_colleges: d.total_colleges,
          colleges: [], isExpanded: false, isLoading: false, isLoaded: false,
        })));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Load department data for a college (used in both tabs)
  const loadDepts = useCallback(async (collegeId: number) => {
    const cacheKey = `dept-${collegeId}-${year}-${community}-${branchFilter}`;
    if (deptData.has(cacheKey)) return; // already loaded

    setDeptLoading(prev => new Set([...prev, collegeId]));
    try {
      const params: any = { year, community, college_id: collegeId };
      if (branchFilter) params.branch_code = branchFilter;
      const res = await axios.get('/cutoffs/district-details', { params });
      const colleges: any[] = res.data.colleges || [];
      const departments: Department[] = colleges.length > 0 ? (colleges[0].departments || []) : [];
      setDeptData(prev => new Map(prev).set(cacheKey, departments));
    } catch {
      setDeptData(prev => new Map(prev).set(cacheKey, []));
    } finally {
      setDeptLoading(prev => { const s = new Set(prev); s.delete(collegeId); return s; });
    }
  }, [year, community, branchFilter, deptData]);

  const toggleCollege = useCallback((collegeId: number) => {
    setExpandedColleges(prev => {
      const next = new Set(prev);
      if (next.has(collegeId)) {
        next.delete(collegeId);
      } else {
        next.add(collegeId);
        loadDepts(collegeId);
      }
      return next;
    });
  }, [loadDepts]);

  // Load colleges inside a district
  const loadDistrictColleges = useCallback(async (districtName: string, districtId: number) => {
    const cacheKey = `${year}-${community}-${collegeType}-${branchFilter}-${districtName}`;
    if (districtCache.has(cacheKey)) {
      setDistricts(prev => prev.map(d =>
        d.district_id === districtId
          ? { ...d, colleges: districtCache.get(cacheKey)!, isLoaded: true, isLoading: false }
          : d
      ));
      return;
    }
    setDistricts(prev => prev.map(d => d.district_id === districtId ? { ...d, isLoading: true } : d));
    try {
      const params: any = { year, community, district: districtName };
      if (collegeType) params.college_type = collegeType;
      if (branchFilter) params.branch_code = branchFilter;
      const res = await axios.get('/cutoffs/district-details', { params });
      const raw: any[] = res.data.colleges || [];
      const colleges: CollegeRow[] = raw.map((c: any, i: number) => ({
        rank: i + 1,
        college_id: c.id || 0,
        college_name: c.college_name || '',
        college_code: c.college_code || '',
        college_type: c.college_type || '',
        district_name: districtName,
        naac_grade: c.naac_grade || null,
        avg_closing_rank: c.avg_rank_across_depts ? Math.round(c.avg_rank_across_depts) : 0,
        total_departments: c.total_departments || 0,
      }));
      setDistrictCache(prev => new Map(prev).set(cacheKey, colleges));
      setDistricts(prev => prev.map(d =>
        d.district_id === districtId ? { ...d, colleges, isLoaded: true, isLoading: false } : d
      ));
    } catch {
      setDistricts(prev => prev.map(d =>
        d.district_id === districtId ? { ...d, isLoading: false } : d
      ));
    }
  }, [year, community, collegeType, branchFilter, districtCache]);

  const toggleDistrict = (districtId: number, districtName: string) => {
    const group = districts.find(d => d.district_id === districtId);
    if (!group) return;
    if (!group.isExpanded && !group.isLoaded) loadDistrictColleges(districtName, districtId);
    setDistricts(prev => prev.map(d =>
      d.district_id === districtId ? { ...d, isExpanded: !d.isExpanded } : d
    ));
  };

  const toggleSelect = (e: React.MouseEvent, col: CollegeRow) => {
    e.preventDefault(); e.stopPropagation();
    const m = new Map(selected);
    if (m.has(col.college_id)) { m.delete(col.college_id); }
    else { if (m.size >= 5) return; m.set(col.college_id, col); }
    setSelected(m);
  };

  const goCompare = () =>
    navigate(`/cutoffs?colleges=${Array.from(selected.keys()).join(',')}&community=${community}`);

  // ---- Department panel (shared between both tabs) ----
  const DeptPanel = ({ collegeId }: { collegeId: number }) => {
    const cacheKey = `dept-${collegeId}-${year}-${community}-${branchFilter}`;
    const isLoading = deptLoading.has(collegeId);
    const depts = deptData.get(cacheKey);

    if (isLoading) {
      return (
        <div className="px-4 pb-4 space-y-2 animate-pulse">
          {[1,2,3].map(i => (
            <div key={i} className="flex justify-between p-3 border border-gray-100 rounded-lg">
              <div className="h-5 bg-gray-200 rounded w-1/2" />
              <div className="h-5 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      );
    }

    if (!depts) return null;

    if (depts.length === 0) {
      return <p className="px-4 pb-4 text-sm text-gray-400">No department data available.</p>;
    }

    return (
      <div className="px-4 pb-4">
        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
          <FaGraduationCap className="text-blue-500" />
          Departments — Last Admitted Rank
        </h3>
        <div className="space-y-2">
          {depts.map((dept, i) => {
            const comp = getCompLabel(dept.avg_closing_rank);
            return (
              <div key={`${dept.branch_code}-${i}`}
                className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:shadow-sm transition-shadow bg-white">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-400">#{i+1}</span>
                    <span className="text-sm font-semibold text-gray-900">{dept.branch_name}</span>
                    <span className="text-xs text-gray-400">({dept.branch_code})</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${comp.bg} ${comp.color} border ${comp.border}`}>
                      {comp.text}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-50 text-blue-700 border border-blue-100">
                      {dept.seats_filled}/{dept.total_seats} seats ({dept.fill_rate}%)
                    </span>
                    {dept.rank_change_5yr !== 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] flex items-center gap-1 ${
                        dept.rank_change_5yr > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                      }`}>
                        {dept.rank_change_5yr > 0
                          ? <><FaArrowUp className="w-2.5 h-2.5" /> Harder (5yr)</>
                          : <><FaArrowDown className="w-2.5 h-2.5" /> Easier (5yr)</>}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right ml-4 flex-shrink-0">
                  <p className="text-[10px] text-gray-400">Last Rank</p>
                  <p className="text-base font-bold text-blue-600">{dept.avg_closing_rank?.toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- College card (clickable, expandable) ----
  const CollegeCard = ({ col, showRank = true }: { col: CollegeRow; showRank?: boolean }) => {
    const comp = getCompLabel(col.avg_closing_rank);
    const isExpanded = expandedColleges.has(col.college_id);
    const isSelected = selected.has(col.college_id);

    return (
      <div className={`bg-white border rounded-xl overflow-hidden transition-all ${
        isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-100 hover:shadow-md hover:border-blue-200'
      }`}>
        {/* Header row — click to expand/collapse */}
        <div
          className="flex items-start gap-3 p-4 cursor-pointer"
          onClick={() => toggleCollege(col.college_id)}
        >
          {showRank && (
            <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
              {col.rank <= 3
                ? <FaMedal className={`text-2xl ${medalColor(col.rank)}`} />
                : <span className="text-sm font-bold text-gray-400">#{col.rank}</span>}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm leading-snug hover:text-blue-700 mb-1">
              {shortName(col.college_name)}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FaMapMarkerAlt className="text-[10px]" />{col.district_name}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeColor[col.college_type] || 'bg-gray-100 text-gray-600'}`}>
                {col.college_type === 'Anna Univ - Constituent' ? 'Anna University' : col.college_type}
              </span>
              {col.naac_grade && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${naacColor[col.naac_grade] || 'bg-gray-200 text-gray-700'}`}>
                  NAAC {col.naac_grade}
                </span>
              )}
            </div>
          </div>

          {/* Right side */}
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <div className="text-right">
              <p className="text-[10px] text-gray-400">Last Rank</p>
              <p className="text-base font-bold text-gray-800">{col.avg_closing_rank.toLocaleString()}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${comp.bg} ${comp.color} ${comp.border}`}>
              {comp.text}
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              {/* Compare toggle */}
              <button
                onClick={(e) => toggleSelect(e, col)}
                className={`p-1.5 rounded-full border transition-all ${
                  isSelected
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-600'
                }`}
                title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
              >
                {isSelected ? <FaCheck className="w-2.5 h-2.5" /> : <FaPlus className="w-2.5 h-2.5" />}
              </button>
              {/* View details link */}
              <Link
                to={`/colleges/${col.college_code}`}
                onClick={e => e.stopPropagation()}
                className="text-[10px] text-blue-500 hover:text-blue-700 hover:underline whitespace-nowrap"
              >
                Full Details
              </Link>
              {/* Expand chevron */}
              <span className="text-gray-400 text-xs">
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </div>
          </div>
        </div>

        {/* Department panel */}
        {isExpanded && (
          <div className="border-t border-gray-100 bg-gray-50">
            <DeptPanel collegeId={col.college_id} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{`Best Engineering Colleges in Tamil Nadu ${SITE_CONFIG.year} — Rankings by Cutoff, District & Community | ChooseMyCollege`}</title>
        <meta name="description" content={`Best engineering colleges in Tamil Nadu for TNEA ${SITE_CONFIG.year} admissions, ranked by ${CUTOFF_END_YEAR} closing rank. Filter by district, community, college type. Top colleges in Chennai, Coimbatore, Madurai and all 38 districts — OC, BC, MBC, SC.`} />
        <meta name="keywords" content={`best engineering colleges Tamil Nadu ${SITE_CONFIG.year}, top TNEA colleges, best colleges Chennai, best colleges Coimbatore, district wise colleges Tamil Nadu, TNEA rank list ${CUTOFF_END_YEAR}`} />
        <meta property="og:title" content={`Best Engineering Colleges in Tamil Nadu ${SITE_CONFIG.year} — TNEA Rankings | ChooseMyCollege`} />
        <meta property="og:description" content={`Top engineering colleges in Tamil Nadu for TNEA ${SITE_CONFIG.year}, ranked by ${CUTOFF_END_YEAR} closing rank. Filter by district, community, type. All 38 districts.`} />
        <meta property="og:url" content="https://choosemycollege.in/best-colleges" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://choosemycollege.in/best-colleges" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4">
        {/* Page Header */}
        <div className="max-w-5xl mx-auto mb-6">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <FaTrophy className="text-3xl text-yellow-500 flex-shrink-0" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Best Colleges in Tamil Nadu</h1>
                <p className="text-gray-500 text-sm mt-0.5">
                  Ranked by TNEA closing rank · Click any college to see department-wise cutoffs
                </p>
              </div>
            </div>

            {/* Tab Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setTab('overall')}
                className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  tab === 'overall' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FaTrophy className="inline mr-1.5 text-xs" />
                Best in Tamil Nadu
              </button>
              <button
                onClick={() => setTab('district')}
                className={`flex-1 md:flex-none px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  tab === 'district' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FaMapMarkerAlt className="inline mr-1.5 text-xs" />
                By District
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-5xl mx-auto mb-5">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center gap-1.5 mb-3">
              <FaFilter className="text-gray-400 text-xs" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Filters</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Community</label>
                <select value={community} onChange={e => setCommunity(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent">
                  {communities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <select value={year} onChange={e => setYear(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent">
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Branch</label>
                <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent">
                  <option value="">All Branches</option>
                  {branchOptions.map(b => <option key={b.branch_code} value={b.branch_code}>{b.display_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">District</label>
                <select value={districtFilter} onChange={e => setDistrictFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent">
                  <option value="">All Districts</option>
                  {districtOptions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs text-gray-500 mb-1">College Type</label>
                <select value={collegeType} onChange={e => setCollegeType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent">
                  <option value="">All Types</option>
                  {collegeTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Compare Bar */}
        {selected.size > 0 && (
          <div className="max-w-5xl mx-auto mb-4">
            <div className="bg-blue-600 text-white rounded-xl px-4 py-3 flex items-center justify-between shadow-lg">
              <div className="flex flex-wrap gap-2 items-center min-w-0">
                <span className="font-semibold text-sm flex-shrink-0">{selected.size} selected:</span>
                {Array.from(selected.values()).map((c: any) => (
                  <span key={c.college_id} className="bg-white/20 px-2 py-0.5 rounded-full text-xs truncate max-w-[140px]">
                    {shortName(c.college_name)}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <button onClick={goCompare}
                  className="bg-white text-blue-600 font-semibold text-xs px-3 py-1.5 rounded-lg hover:bg-blue-50">
                  Compare
                </button>
                <button onClick={() => setSelected(new Map())} className="text-white/70 hover:text-white text-xl leading-none">×</button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-5xl mx-auto">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <FaSpinner className="animate-spin text-blue-600 text-4xl" />
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-600">{error}</div>
          )}

          {/* Overall Rankings */}
          {!loading && !error && tab === 'overall' && (
            <div className="space-y-3">
              {overall.length > 0 && (
                <JsonLd data={buildItemListSchema(
                  overall.slice(0, 20).map(col => ({
                    college_id: col.college_id,
                    college_name: col.college_name,
                    college_code: col.college_code,
                    district_name: col.district_name,
                    avg_closing_rank: col.avg_closing_rank,
                  })),
                  `Best Engineering Colleges in Tamil Nadu ${year} — ${community} Community`
                )} />
              )}
              {overall.length === 0 && (
                <p className="text-center text-gray-500 py-12">No data for this filter combination.</p>
              )}
              {overall.map(col => <CollegeCard key={col.college_id} col={col} showRank />)}
              {overall.length > 0 && (
                <p className="text-center text-xs text-gray-400 py-4">
                  {overall.length} colleges · {community} community · {year} · Ranked by avg last admitted rank
                </p>
              )}
            </div>
          )}

          {/* District View */}
          {!loading && !error && tab === 'district' && (
            <div className="space-y-3">
              {districts.length === 0 && (
                <p className="text-center text-gray-500 py-12">No districts found.</p>
              )}
              {districts.map((group, i) => (
                <div key={group.district_id} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <button
                    className="w-full text-left p-4 md:p-5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 transition-all"
                    onClick={() => toggleDistrict(group.district_id, group.district_name)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-white bg-white/20 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0">
                          #{i + 1}
                        </span>
                        <div>
                          <h2 className="text-base md:text-lg font-bold text-white flex items-center gap-2">
                            <FaMapMarkerAlt className="text-sm flex-shrink-0" />
                            {group.district_name}
                          </h2>
                          <p className="text-white/75 text-sm">{group.total_colleges} colleges</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-white">
                        {group.isLoading && <FaSpinner className="animate-spin" />}
                        {group.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    </div>
                  </button>

                  {group.isExpanded && (
                    <div className="p-4 space-y-3">
                      {group.isLoading && (
                        <div className="flex justify-center py-6">
                          <FaSpinner className="animate-spin text-indigo-500 text-2xl" />
                        </div>
                      )}
                      {group.isLoaded && group.colleges.length === 0 && (
                        <p className="text-center text-gray-400 py-4 text-sm">No colleges for this filter.</p>
                      )}
                      {group.isLoaded && group.colleges.map(col => (
                        <CollegeCard key={col.college_id} col={col} showRank />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SEO content block */}
        {!loading && tab === 'overall' && overall.length > 0 && (
          <div className="max-w-5xl mx-auto mt-10 bg-white rounded-2xl shadow-md p-6 md:p-8">
            <h2 className="text-lg font-bold text-gray-800 mb-3">About These Rankings</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-3">
              Rankings are based on average last admitted rank (closing rank) across all departments for <strong>{community}</strong> community in <strong>{year}</strong>. A lower closing rank means more students competed — indicating higher demand.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Use the <Link to="/predictor" className="text-blue-600 hover:underline">College Predictor</Link> to find colleges matching your rank, or <Link to="/cutoffs" className="text-blue-600 hover:underline">Compare Colleges</Link> for side-by-side cutoff trends.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default BestColleges;
